import type { Locator } from '../types';

/** CSS.escape with a safe fallback for non-browser/older environments. */
function cssEscape(value: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(value);
  }
  return value.replace(/[^a-zA-Z0-9_-]/g, (ch) => `\\${ch}`);
}

/**
 * Build a reasonably stable CSS selector for an element. Prefers a unique id,
 * otherwise walks up the tree building an :nth-of-type path until it reaches a
 * stable anchor (an element with an id) or the document body.
 */
export function buildSelector(el: Element): string {
  if (el.id && document.querySelectorAll(`#${cssEscape(el.id)}`).length === 1) {
    return `#${cssEscape(el.id)}`;
  }

  const parts: string[] = [];
  let node: Element | null = el;

  while (node && node.nodeType === Node.ELEMENT_NODE && node !== document.body) {
    if (node.id && document.querySelectorAll(`#${cssEscape(node.id)}`).length === 1) {
      parts.unshift(`#${cssEscape(node.id)}`);
      return parts.join(' > ');
    }

    const tag = node.tagName.toLowerCase();
    const parent: Element | null = node.parentElement;

    if (!parent) {
      parts.unshift(tag);
      break;
    }

    const sameTag = Array.from(parent.children).filter(
      (c) => c.tagName === node!.tagName,
    );
    if (sameTag.length === 1) {
      parts.unshift(tag);
    } else {
      const index = sameTag.indexOf(node) + 1;
      parts.unshift(`${tag}:nth-of-type(${index})`);
    }

    node = parent;
  }

  return ['body', ...parts].join(' > ');
}

/**
 * Capture a locator from a click: a selector for the target element, the
 * relative position within it, and absolute page coordinates as a fallback.
 */
export function captureLocator(el: Element, clientX: number, clientY: number): Locator {
  const rect = el.getBoundingClientRect();
  const ratioX = rect.width > 0 ? (clientX - rect.left) / rect.width : 0.5;
  const ratioY = rect.height > 0 ? (clientY - rect.top) / rect.height : 0.5;

  return {
    selector: buildSelector(el),
    ratio_x: clamp(ratioX),
    ratio_y: clamp(ratioY),
    abs_x: Math.round(clientX + window.scrollX),
    abs_y: Math.round(clientY + window.scrollY),
  };
}

/**
 * Resolve a stored locator to absolute page coordinates for the current layout.
 * Returns null when the element is gone AND there is no usable fallback.
 */
export function resolveLocator(locator: Locator | null, fallbackAbs?: { x: number; y: number }): { x: number; y: number } | null {
  if (locator?.selector) {
    try {
      const el = document.querySelector(locator.selector);
      if (el) {
        const rect = el.getBoundingClientRect();
        return {
          x: Math.round(rect.left + window.scrollX + locator.ratio_x * rect.width),
          y: Math.round(rect.top + window.scrollY + locator.ratio_y * rect.height),
        };
      }
    } catch {
      // Invalid selector — fall through to absolute coordinates.
    }
  }

  if (locator) {
    return { x: locator.abs_x, y: locator.abs_y };
  }

  if (fallbackAbs) {
    return fallbackAbs;
  }

  return null;
}

function clamp(n: number): number {
  return Math.max(0, Math.min(1, Number.isFinite(n) ? n : 0.5));
}
